import { ActionType } from "../types/store-types";
import { IError } from "../types/errors";

/* - Data Types - */

export const timeZoneOptions = {
    UTC4: "( UTC + 04:00 )",
    UTC_12: "( UTC - 12:00 )",
    UTC_11: "( UTC - 11:00 )",
    UTC_10: "( UTC - 10:00 )",
    UTC_9_30: "( UTC - 09:30 )",
    UTC_9: "( UTC - 09:00 )",
    UTC_8: "( UTC - 08:00 )",
    UTC_7: "( UTC - 07:00 )",
    UTC_6: "( UTC - 06:00 )",
    UTC_5: "( UTC - 05:00 )",
    UTC_4: "( UTC - 04:00 )",
    UTC_3_30: "( UTC - 03:30 )",
    UTC_3: "( UTC - 03:00 )",
    UTC_2: "( UTC - 02:00 )",
    UTC_1: "( UTC - 01:00 )",
    UTC00: "( UTC + 00:00 )",
    UTC1: "( UTC + 01:00 )",
    UTC2: "( UTC + 02:00 )",
    UTC3: "( UTC + 03:00 )",
    UTC3_30: "( UTC + 03:30 )",
    UTC4_30: "( UTC + 04:30 )",
    UTC5: "( UTC + 05:00 )",
    UTC5_30: "( UTC + 05:30 )",
    UTC5_45: "( UTC + 05:45 )",
    UTC6: "( UTC + 06:00 )",
    UTC6_30: "( UTC + 06:30 )",
    UTC7: "( UTC + 07:00 )",
    UTC8: "( UTC + 08:00 )",
    UTC8_45: "( UTC + 08:45 )",
    UTC9: "( UTC + 09:00 )",
    UTC9_45: "( UTC + 09:45 )",
    UTC10: "( UTC + 10:00 )",
    UTC10_30: "( UTC + 10:30 )",
    UTC11: "( UTC + 11:00 )",
    UTC12: "( UTC + 12:00 )",
    UTC12_45: "( UTC + 12:45 )",
    UTC13: "( UTC + 13:00 )",
    UTC14: "( UTC + 14:00 )",
};

export const languageOptions = {
    english: "English",
};

export enum CaptchaTypes {
    CHECKBOX = "CHECKBOX",
    INVISIBLE = "INVISIBLE",
}

export interface IFormSettings {
    native: {
        currency: string;
        priceFormat: string;
    };
    general: {
        allowMultipleSubmissions: boolean;
        showFormTitle: boolean;
        setPassword: {
            isActive: boolean;
            password: string;
        };
        showCaptcha: CaptchaTypes;
        timeZone: string;
        language: string;
    };
    limits: {
        formAvailability: {
            isActive: boolean;
            disableBy: {
                date: {
                    isActive: boolean;
                    startDate: string;
                    endDate: string;
                    warning: string;
                };
                submission: {
                    isActive: boolean;
                    submissionsNumber: number;
                    limitPeriod: string;
                    limitMonths: number;
                    warning: string;
                };
                perIP: {
                    isActive: boolean;
                    warning: string;
                    submissionLimit: number;
                    interval: string;
                };
            };
        };
    };
    notification: {
        pushNotification: {
            isActive: boolean;
            title: string;
            message: string;
        };
        emailNotification: {
            isActive: boolean;
            title: string;
            to: string;
            json: object;
            message: string;
        };
        emailToRespondent: {
            isActive: boolean;
            subject: string;
            senderName: string;
            to: string;
            replyToEmail: string;
            json: object;
            message: string;
        };
    };
    saveProgress: {
        saveAndResume: boolean;
        allowThePerson: {
            isActive: boolean;
            emailSubject: string;
            message: string;
            json: object;
        };
        storeSubmission: {
            isActive: boolean;
            loadButtonLabel: string;
            saveButtonLabel: string;
        };
    };
    confirmation: {
        afterSubmit: {
            isActive: boolean;
            enterURL: string;
            openURL: boolean;
            message: string;
            json: object;
            addMoreAnswer: {
                isActive: boolean;
                buttonLabel: string;
            };
            downloadPDF: {
                isActive: boolean;
                buttonLabel: string;
            };
            editAnswer: {
                isActive: boolean;
                buttonLabel: string;
            };
        };
    };
}

export interface IInitFormSettings {
    settings: IFormSettings;
}

export interface IChangeFormSettingsRequest {
    [key: string]: string;
}

export interface IEmailEditorData {
    html: string;
    json: object;
}

export interface IChangeFormSettingsSuccess {}

export interface IChangeFormSettingsFailure extends IError {
    prevState: IFormSettings;
}

export interface IChangeGeneralSetting {
    prevState: IFormSettings["general"];
    currentState: IFormSettings["general"];
}

export interface IChangeNativeSettings {
    prevState: IFormSettings["native"];
    currentState: IFormSettings["native"];
}

export interface IChangeLimitsSetting {
    prevState: IFormSettings["limits"];
    currentState: IFormSettings["limits"];
}

export interface IChangeNotificationSetting {
    prevState: IFormSettings["notification"];
    currentState: IFormSettings["notification"];
}

export interface IChangeSaveProgressSetting {
    prevState: IFormSettings["saveProgress"];
    currentState: IFormSettings["saveProgress"];
}

export interface IChangeConfirmationSetting {
    prevState: IFormSettings["confirmation"];
    currentState: IFormSettings["confirmation"];
}
/* - Action Types - */

export enum FormSettingsTypes {
    INIT_FORM_SETTINGS = "SETTINGS.INIT_FORM_SETTINGS",
    CHANGE_FORM_SETTINGS_REQUEST = "SETTINGS.CHANGE_FORM_SETTINGS_REQUEST",
    CHANGE_FORM_SETTINGS_SUCCESS = "SETTINGS.CHANGE_FORM_SETTINGS_SUCCESS",
    CHANGE_FORM_SETTINGS_FAILURE = "SETTINGS.CHANGE_FORM_SETTINGS_FAILURE",
    CHANGE_GENERAL_SETTING_REQUEST = "SETTINGS.CHANGE_GENERAL_SETTING_REQUEST",
    CHANGE_NATIVE_SETTING_REQUEST = "SETTINGS.CHANGE_NATIVE_SETTINGS_REQUEST",
    CHANGE_LIMITS_SETTING = "SETTINGS.CHANGE_LIMITS_SETTING",
    CHANGE_NOTIFICATIONS_SETTING = "SETTINGS.CHANGE_NOTIFICATIONS_SETTING",
    CHANGE_CONFIRMATION_SETTING = "SETTINGS.CHANGE_CONFIRMATION_SETTING",
    CHANGE_SAVE_PROGRESS_SETTING = "SETTINGS.CHANGE_SAVE_PROGRESS_SETTING",
}

/* - Action Interfaces  - */

export type InitFormSettingsActionType = ActionType<FormSettingsTypes.INIT_FORM_SETTINGS, IInitFormSettings>;

export type ChangeFormSettingsRequestActionType = ActionType<
    FormSettingsTypes.CHANGE_FORM_SETTINGS_REQUEST,
    IChangeFormSettingsRequest
>;

export type ChangeFormSettingsSuccessActionType = ActionType<
    FormSettingsTypes.CHANGE_FORM_SETTINGS_SUCCESS,
    IChangeFormSettingsSuccess
>;
export type ChangeFormSettingsFailureActionType = ActionType<
    FormSettingsTypes.CHANGE_FORM_SETTINGS_FAILURE,
    IChangeFormSettingsFailure
>;

export type ChangeGeneralSettingActionType = ActionType<
    FormSettingsTypes.CHANGE_GENERAL_SETTING_REQUEST,
    IChangeGeneralSetting
>;
export type ChangeLimitSettingsActionType = ActionType<FormSettingsTypes.CHANGE_LIMITS_SETTING, IChangeLimitsSetting>;

export type ChangeNotificationSettingsActionType = ActionType<
    FormSettingsTypes.CHANGE_NOTIFICATIONS_SETTING,
    IChangeNotificationSetting
>;

export type ChangeSaveProgressSettingsActionType = ActionType<
    FormSettingsTypes.CHANGE_SAVE_PROGRESS_SETTING,
    IChangeSaveProgressSetting
>;

export type ChangeConfirmationSettingsActionType = ActionType<
    FormSettingsTypes.CHANGE_CONFIRMATION_SETTING,
    IChangeConfirmationSetting
>;

export type ChangeNativeFormSettingsRequestActionType = ActionType<
    FormSettingsTypes.CHANGE_NATIVE_SETTING_REQUEST,
    IChangeNativeSettings
>;
